import React, { useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  useTheme,
  Avatar,
  ListItemAvatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'; // Fallback icon
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setPanelOpen, markAsRead, markAllAsRead, setNotifications } from '../../store/slices/notificationSlice';
import {
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetNotificationsQuery,
} from '../../api/notificationApiSlice';
// import { formatDistanceToNow } from 'date-fns';

const timeAgo = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const NotificationPanel: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { isOpen, items } = useSelector((state: RootState) => state.notifications);

  // API Mutations & Queries
  const { data: notificationsData, refetch } = useGetNotificationsQuery({
    limit: 50,
    offset: 0,
    notification_type: 'realtime',
  });
  const [markReadApi] = useMarkNotificationAsReadMutation();
  const [markAllReadApi] = useMarkAllNotificationsAsReadMutation();

  // Load initial notifications
  useEffect(() => {
    if (notificationsData) {
      dispatch(setNotifications(notificationsData));
    }
  }, [notificationsData, dispatch]);

  // Refetch when panel opens
  // useEffect(() => {
  //   if (isOpen) {
  //     refetch();
  //   }
  // }, [isOpen, refetch]);

  const handleClose = () => {
    dispatch(setPanelOpen(false));
  };

  const handleMarkAsRead = async (id: string) => {
    dispatch(markAsRead(id)); // Optimistic UI update
    await markReadApi(id);
  };

  const handleMarkAllAsRead = async () => {
    dispatch(markAllAsRead());
    await markAllReadApi();
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: 400,
            maxWidth: '100%',
            mt: '64px', // Offset for topbar (adjust based on your actual topbar height)
            height: 'calc(100% - 64px)',
            boxShadow: '-4px 0px 20px rgba(0,0,0,0.1)',
            borderLeft: `1px solid ${theme.palette.divider}`,
          },
        },
        backdrop: {
          sx: {
            mt: '64px',
            height: 'calc(100% - 64px)',
            backgroundColor: 'transparent', // Make it look like a floating panel
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          <Link
            component="button"
            variant="caption"
            onClick={handleMarkAllAsRead}
            sx={{ textDecoration: 'none', color: 'primary.main', cursor: 'pointer' }}
          >
            Mark all as read
          </Link>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Notification List */}
      <List sx={{ p: 0, overflowY: 'auto', flex: 1, bgcolor: '#f5f7fa' }}>
        {items.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: 'text.secondary',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <NotificationsNoneIcon sx={{ fontSize: 48, opacity: 0.5 }} />
            <Typography>No notifications</Typography>
          </Box>
        ) : (
          items.map((item) => (
            <React.Fragment key={item.notification_id}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  bgcolor: item.delivery_status !== 'read' ? '#e3f2fd' : 'background.paper', // Light blue for unread
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': { bgcolor: item.delivery_status !== 'read' ? '#bbdefb' : 'action.hover' },
                  px: 2,
                  py: 1.5,
                }}
                onClick={() => handleMarkAsRead(item.notification_id)}
              >
                <ListItemAvatar sx={{ minWidth: 48 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: item.delivery_status !== 'read' ? 'primary.main' : 'grey.400',
                    }}
                    src={undefined} // We don't have user avatars yet, pass undefined
                  >
                    {/* Fallback to first letter of title or 'N' */}
                    {/* <PersonIcon /> */}N
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        lineHeight: 1.4,
                        mb: 0.5,
                      }}
                    >
                      {item.message}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {timeAgo(item.created_at)}
                    </Typography>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))
        )}
      </List>
    </Drawer>
  );
};

export default NotificationPanel;
